// ---------------------------------------------------------------------------
// Renderer: Gantt / Workplan
//
// Swim-lane workplan chart. Groups (phases) stack vertically; each group has
// a header row, then per workstream: a small lane header followed by ONE ROW
// PER TASK. A bold horizontal rule separates one lane from the next; a
// lighter rule separates tasks within the same lane. Vertical week
// separators run the full height of the chart (not just the ruler). An
// outputs footnote closes out each group. Groups with nothing scheduled
// (e.g. an uncommitted next phase with no workstreams yet) are simply not
// included by the crystallizer, so this renderer never needs a
// placeholder/empty-state treatment for them.
// ---------------------------------------------------------------------------

import Konva from "konva";
import CustomText from "../CustomText";
import { themeColor, defaultTheme } from "./plainObjectRenderer";
import { addDebugOverlay } from "./helpers.js";

const LANE_COLOR_KEYS = ["accent", "accent2", "dark", "muted"];
const LANE_COLOR_FALLBACKS = ["#2C4A6E", "#4A6E8E", "#6E8EAE", "#8EAECC"];
// Rounded corners read as 30% smaller than a "full" pill — sharper, closer
// to a data-grid block than a soft badge.
const CORNER_SCALE = 0.7;
// Shared light grid line (week separators + task separators) — one
// consistent faintness for both, a shade darker than a barely-visible grid.
const GRIDLINE_COLOR = "#d6d6d6";
// Bolder rule marking the boundary between one swim lane and the next.
const LANE_SEPARATOR_COLOR = "#b0b0b0";

function laneColor(theme, index) {
  const i = index % LANE_COLOR_KEYS.length;
  return themeColor(theme, LANE_COLOR_KEYS[i], LANE_COLOR_FALLBACKS[i]);
}

// Smallest week-label stride that keeps adjacent labels from overlapping,
// given each week is weekW px wide. Never skips more than every 4th week —
// beyond that we're not showing "all weeks", we're showing a sparse sample.
function pickWeekStride(weekW, s) {
  const minLabelPx = 30 * s;
  for (const stride of [1, 2, 3, 4]) {
    if (weekW * stride >= minLabelPx) return stride;
  }
  return 4;
}

// Measures how tall a task's wrapped label will actually render at the
// label column's width — a throwaway node, never added to the group. Task
// text length varies a lot (a one-line task vs. a full sentence), so a
// fixed row height either wastes space or overflows into the next row;
// this is the one source of truth both the height pre-pass and the real
// draw loop use, so they never disagree.
function measureTaskRowHeight(bar, sizes) {
  const probe = new CustomText({
    x: 0, y: 0, width: sizes.taskLabelW,
    text: bar.label || "", fontSize: sizes.taskLabelFontSize, lineHeight: 1.25,
  });
  const measured = probe.height();
  probe.destroy();
  const verticalPad = Math.round(8 * sizes.taskS);
  return Math.max(sizes.taskRowMinH, Math.ceil(measured) + verticalPad);
}

// Sums the same row increments the draw loop below produces, without
// touching Konva — used to size the full-height vertical week separators
// before any lane content is drawn (so the lines sit behind it). Returns
// BOTH the full content height (for group.height()/overall layout) and
// gridEndH — the height right after the LAST group's own lane content,
// before its trailing outputs footnote and gap. The week grid is a
// property of the schedule (lanes/bars), not of the footnote text below
// it — running gridlines under the OUTPUTS line makes it look like part
// of the chart when it's just an annotation underneath it.
function estimateContentHeight(data, sizes) {
  const { rulerH, groupHeaderH, laneHeaderH, outputsRowH, groupGapY } = sizes;
  let h = rulerH + Math.round(6 * sizes.s);
  let gridEndH = h;
  for (const g of data.groups) {
    h += groupHeaderH;
    for (const lane of g.lanes) {
      h += laneHeaderH;
      for (const bar of lane.bars) h += measureTaskRowHeight(bar, sizes);
    }
    gridEndH = h;
    if (Array.isArray(g.outputs) && g.outputs.length > 0) h += outputsRowH;
    h += groupGapY;
  }
  return { totalH: h, gridEndH };
}

export function renderGantt(block, crystalizedData, options) {
  const { x = 0, y = 0, width = 800, height, imageCallback, theme, fontScale = 1 } = options;
  const s = fontScale;
  const themeColors = theme || defaultTheme;
  const group = new Konva.Group({ x, y, width });
  const data = crystalizedData[block.id];

  if (!data || !Array.isArray(data.groups) || data.groups.length === 0) {
    const h = Math.round(70 * s);
    const isPreview = options._preview;
    group.add(new Konva.Rect({ width, height: h, fill: "#f7f7f7", stroke: "#e5e5e5", strokeWidth: 1 }));
    group.add(new CustomText({
      x: Math.round(10 * s), y: h / 2 - Math.round(9 * s), width: width - Math.round(20 * s),
      text: isPreview ? "Workplan (populated from the engagement plan)" : "No schedule data",
      fontSize: Math.round(13 * s), fill: "#999", align: "center",
      refreshCallback: imageCallback,
    }));
    group.height(h);
    addDebugOverlay(group, block, { ...options, height: h });
    return group;
  }

  const totalWeeks = Math.max(1, data.total_weeks || 1);
  const axisMax = Math.max(totalWeeks, ...data.groups.map((g) => g.end || 0));

  const labelColW = Math.round(width * (block.label_column_width ?? 0.3));
  const barAreaX = labelColW;
  const barAreaW = Math.max(1, width - labelColW);
  const weekW = barAreaW / axisMax;
  const xForWeek = (wk) => barAreaX + (wk - 1) * weekW;

  // taskScale shrinks ONLY the task rows (font size + row height) — the
  // ruler/group/lane headers stay put. This block reports its own natural
  // height to a proportional parent slot (the layout engine's overflow-shrink
  // only fires for sizing:"content" flows, never a fixed proportion), so with
  // many/long tasks it can render taller than its allocated box and get
  // cropped. When a real, finite allocation comes in via `height`, binary
  // search the largest taskScale (down to a floor) whose estimated content
  // height still fits — same technique VizDslLayoutEngine's Phase 3 uses.
  const buildSizes = (taskScale) => {
    const taskS = s * taskScale;
    return {
      s, taskS,
      rulerH: Math.round(24 * s),
      groupHeaderH: Math.round(22 * s),
      laneHeaderH: Math.round(18 * s),
      taskRowMinH: Math.round((block.task_row_height || 30) * taskS),
      taskLabelW: labelColW - Math.round(16 * s),
      taskLabelFontSize: Math.round(10.5 * taskS),
      outputsRowH: Math.round(16 * s),
      groupGapY: Math.round(10 * s),
    };
  };

  const MIN_TASK_SCALE = 0.6;
  let taskScale = 1;
  if (Number.isFinite(height) && height > 0) {
    const naturalH = estimateContentHeight(data, buildSizes(1)).totalH;
    if (naturalH > height) {
      let lo = MIN_TASK_SCALE, hi = 1;
      for (let i = 0; i < 10; i++) {
        const mid = (lo + hi) / 2;
        const trialH = estimateContentHeight(data, buildSizes(mid)).totalH;
        if (trialH <= height) lo = mid; else hi = mid;
      }
      taskScale = lo;
    }
  }

  const sizes = buildSizes(taskScale);
  const { rulerH, groupHeaderH, laneHeaderH, outputsRowH, groupGapY } = sizes;
  const { gridEndH } = estimateContentHeight(data, sizes);

  // ── Vertical week separators — span the actual lane/bar content only
  // (stop before the trailing outputs footnote), drawn first so lane/bar
  // content sits on top. ──
  const stride = pickWeekStride(weekW, s);
  for (let wk = 1; wk <= axisMax; wk++) {
    const bx = xForWeek(wk);
    group.add(new Konva.Line({ points: [bx, 0, bx, gridEndH], stroke: GRIDLINE_COLOR, strokeWidth: 1 }));
  }
  group.add(new Konva.Line({ points: [barAreaX + barAreaW, 0, barAreaX + barAreaW, gridEndH], stroke: GRIDLINE_COLOR, strokeWidth: 1 }));

  // ── Week ruler labels ──
  for (let wk = 1; wk <= axisMax; wk += stride) {
    const bx = xForWeek(wk);
    group.add(new CustomText({
      x: bx + Math.round(3 * s), y: Math.round(4 * s), width: Math.round(weekW * stride),
      text: `WK ${wk}`, fontSize: Math.round(9 * s), fontStyle: "bold",
      fill: themeColor(themeColors, "textSecondary", "#888"), letterSpacing: 0.5,
      refreshCallback: imageCallback,
    }));
  }
  group.add(new Konva.Line({ points: [barAreaX, rulerH, barAreaX + barAreaW, rulerH], stroke: "#d8d8d8", strokeWidth: 1 }));

  let cy = rulerH + Math.round(6 * s);
  let laneColorCursor = 0;

  for (const g of data.groups) {
    if (!g.committed) {
      group.add(new CustomText({
        x: width * 0.3, y: cy, width: width * 0.7,
        text: "NOT COMMITTED · NOT PRICED", fontSize: Math.round(9.5 * s), fontStyle: "italic",
        fill: themeColor(themeColors, "textSecondary", "#888"), align: "right",
        refreshCallback: imageCallback,
      }));
    }
    cy += groupHeaderH;

    for (const lane of g.lanes) {
      const color = laneColor(themeColors, laneColorCursor++);

      group.add(new Konva.Rect({ x: 0, y: cy + Math.round(4 * s), width: Math.round(3 * s), height: laneHeaderH - Math.round(6 * s), fill: color }));
      group.add(new CustomText({
        x: Math.round(8 * s), y: cy + Math.round(2 * s), width: labelColW - Math.round(12 * s),
        text: (lane.label || "").toUpperCase(), fontSize: Math.round(9.5 * s), fontStyle: "bold",
        fill: themeColor(themeColors, "text", "#333"), letterSpacing: 0.5,
        wrap: "none", ellipsis: true,
        refreshCallback: imageCallback,
      }));
      cy += laneHeaderH;

      lane.bars.forEach((bar, ti) => {
        if (ti > 0) {
          group.add(new Konva.Line({ points: [0, cy, width, cy], stroke: GRIDLINE_COLOR, strokeWidth: 1 }));
        }

        const taskRowH = measureTaskRowHeight(bar, sizes);
        const labelX = Math.round(10 * s);
        const labelW = sizes.taskLabelW;
        group.add(new CustomText({
          x: labelX, y: cy + Math.round(4 * s), width: labelW,
          text: bar.label || "", fontSize: sizes.taskLabelFontSize, lineHeight: 1.25,
          fill: themeColor(themeColors, "text", "#222"),
          refreshCallback: imageCallback,
        }));

        const bx = xForWeek(bar.start);
        const bw = Math.max(2, bar.duration * weekW - Math.round(2 * s));
        const barH = Math.round(taskRowH * 0.42);
        const barY = cy + Math.round((taskRowH - barH) / 2);
        const cornerR = Math.round(Math.min(8 * s, barH / 2) * CORNER_SCALE);
        group.add(new Konva.Rect({ x: bx, y: barY, width: bw, height: barH, fill: color, cornerRadius: cornerR }));

        const durationLabel = `${bar.duration}wk`;
        const fitsInside = bw > Math.round(34 * s);
        const afterX = bx + bw + Math.round(6 * s);
        group.add(new CustomText({
          x: fitsInside ? bx : afterX,
          y: barY + Math.round((barH - 11 * sizes.taskS) / 2),
          width: fitsInside ? bw : Math.max(Math.round(30 * s), barAreaX + barAreaW - afterX),
          text: durationLabel, fontSize: Math.round(9.5 * sizes.taskS), fontStyle: "bold",
          fill: fitsInside ? themeColor(themeColors, "darkText", "#fff") : themeColor(themeColors, "text", "#333"),
          align: fitsInside ? "center" : "left",
          refreshCallback: imageCallback,
        }));

        cy += taskRowH;
      });

      group.add(new Konva.Line({ points: [0, cy, width, cy], stroke: LANE_SEPARATOR_COLOR, strokeWidth: 1.5 }));
    }

    if (Array.isArray(g.outputs) && g.outputs.length > 0) {
      group.add(new CustomText({
        x: Math.round(4 * s), y: cy, width,
        text: `OUTPUTS  ${g.outputs.join(" · ")}`,
        fontSize: Math.round(9 * s), fontStyle: "italic",
        fill: themeColor(themeColors, "textSecondary", "#888"),
        wrap: "none", ellipsis: true,
        refreshCallback: imageCallback,
      }));
      cy += outputsRowH;
    }

    cy += groupGapY;
  }

  group.height(cy);
  addDebugOverlay(group, block, { ...options, height: cy });
  return group;
}
